import { useQuery } from '@tanstack/react-query';
import { getTenantConfig } from '../services/tenants';
import { useApiConfig } from './useApiConfig';

/**
 * Hook to fetch tenant configuration using React Query
 * @param {string|null} outletId - The outlet ID
 * @returns {Object} Query result with data, isLoading, isError, etc.
 */
export function useTenantConfig(outletId) {
  const { baseUrl, isUsingNewEndpoints, token } = useApiConfig();

  return useQuery({
    queryKey: ['tenant-config', outletId, baseUrl],
    queryFn: async () => {
      if (!outletId || !isUsingNewEndpoints || !token) {
        return null;
      }
      try {
        const result = await getTenantConfig(outletId, baseUrl);
        return result || null;
      } catch (error) {
        return null;
      }
    },
    enabled: !!outletId && isUsingNewEndpoints && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetches tenant config directly (for use outside React components)
 * @param {string} outletId - The outlet ID
 * @param {string} baseUrl - Base URL for the API
 * @returns {Promise<Array|null>} Promise resolving to tenant config or null
 */
export async function fetchTenantConfig(outletId, baseUrl) {
  if (!outletId || !baseUrl) {
    return null;
  }
  try {
    const result = await getTenantConfig(outletId, baseUrl);
    return result || null;
  } catch (error) {
    return null;
  }
}

/**
 * Extracts the waitlist configuration from tenant config
 * @param {Array|null} tenantConfig - The tenant configuration array
 * @returns {Object|null} The waitlist configuration or null
 */
export function useWaitlistConfig(tenantConfig) {
  if (!tenantConfig || !Array.isArray(tenantConfig)) {
    return null;
  }

  const waitlistConfig = tenantConfig.find((config) => config.configType === 'waitlist');
  return waitlistConfig?.value || null;
}
